import React from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { SummaryData } from '@/services/reportService'
import { reportService } from '@/services/reportService'
import { 
  Clock, 
  FolderOpen, 
  DollarSign, 
  Star,
  TrendingUp,
  BarChart3
} from 'lucide-react'

interface SummaryCardsProps {
  data: SummaryData | undefined
  loading: boolean
}

interface SummaryCardProps {
  title: string
  value: string
  icon: React.ReactNode
  valueColor?: string
  description?: string
}

const SummaryCard: React.FC<SummaryCardProps> = ({ 
  title, 
  value, 
  icon, 
  valueColor = 'text-secondary-800',
  description 
}) => {
  return (
    <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs md:text-sm font-medium text-secondary-600">{title}</p>
          <p className={`text-xl md:text-2xl font-bold ${valueColor}`}>
            {value}
          </p>
          {description && (
            <p className="text-xs md:text-sm text-secondary-500 mt-1">{description}</p>
          )}
        </div>
        <div className="w-6 h-6 md:w-8 md:h-8 text-primary-600">
          {icon}
        </div>
      </div>
    </div>
  )
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ data, loading }) => {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER'

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {Array.from({ length: isAdmin ? 6 : 4 }).map((_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200 animate-pulse">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-3 md:h-4 bg-secondary-200 rounded w-16 md:w-20 mb-2"></div>
                <div className="h-5 md:h-6 bg-secondary-200 rounded w-12 md:w-16 mb-1"></div>
                <div className="h-2 md:h-3 bg-secondary-200 rounded w-10 md:w-12"></div>
              </div>
              <div className="w-6 h-6 md:w-8 md:h-8 bg-secondary-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200 text-center">
        <p className="text-secondary-600">無法載入統計資料</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
      {/* 總工時 - 所有用戶都顯示 */}
      <SummaryCard
        title="總工時"
        value={`${data.totalHours}h`}
        icon={<Clock className="w-full h-full" />}
      />
      
      {/* 參與專案數量 - 所有用戶都顯示 */}
      <SummaryCard
        title="參與專案數量"
        value={`${data.projectCount}個`}
        icon={<FolderOpen className="w-full h-full" />}
      />
      
      {/* 總金額 - 所有用戶都顯示 */}
      <SummaryCard
        title="總金額"
        value={reportService.formatCurrency(data.totalAmount)}
        icon={<DollarSign className="w-full h-full" />}
      />
      
      {/* 平均品質分數 - 僅個人用戶顯示 */}
      {!isAdmin && (
        <SummaryCard
          title="平均品質分數"
          value={data.averageQualityScore ? `${data.averageQualityScore}分` : '未評分'}
          icon={<Star className="w-full h-full" />}
          valueColor={reportService.getQualityScoreColor(data.averageQualityScore)}
          description={reportService.getQualityScoreLabel(data.averageQualityScore)}
        />
      )}
      
      {/* 管理員專屬卡片 */}
      {isAdmin && (
        <>
          <SummaryCard
            title="總花費金額"
            value={reportService.formatCurrency(data.totalTeamCost || 0)}
            icon={<TrendingUp className="w-full h-full" />}
            description="團隊總花費"
          />
          
          <SummaryCard
            title="預算使用率"
            value={`${data.budgetUtilization || 0}%`}
            icon={<BarChart3 className="w-full h-full" />}
            valueColor={
              (data.budgetUtilization || 0) > 90 ? 'text-red-600' :
              (data.budgetUtilization || 0) > 80 ? 'text-yellow-600' :
              'text-green-600'
            }
          />
        </>
      )}
    </div>
  )
}

export default SummaryCards
